// src/components/MyPlans.jsx
import React, { useEffect, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import { Clock, CreditCard, Receipt, UserPlus, Zap, BadgeInfo, Download } from "lucide-react";
import { ADDONS } from "../constants/feature.constants";
import BROXInvoiceGenerator from "./BROX_Invoice_Generator";
import { formatBonusMonths } from "../lib/offers/offerTheme";

// Campaign offer stamped on a PaidAccount by the server, when it had bonus months.
const getAppliedOffer = (item) =>
  Number(item?.appliedOffer?.bonusMonths) > 0 ? item.appliedOffer : null;

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (Number.isNaN(amount)) return "₹0.00";
  return `₹${amount.toFixed(2)}`;
};

const normalize = (value) => String(value || "").trim().toLowerCase();

const getAddonContactInfo = (item) => {
  const addonDetails = Array.isArray(item?.AddonsArray) ? item.AddonsArray[0] : null;
  if (!addonDetails) {
    return { value: item?.Value || "0", sourceLabel: "" };
  }

  if (Number(addonDetails.OwnerTotalContactLimit) > 0) {
    return {
      value: addonDetails.OwnerTotalContactLimit,
      sourceLabel: "Owner Property",
    };
  }

  if (Number(addonDetails.BrokerTotalContactLimit) > 0) {
    return {
      value: addonDetails.BrokerTotalContactLimit,
      sourceLabel: "Broker Property",
    };
  }

  if (Number(addonDetails.BuilderTotalContactLimit) > 0) {
    return {
      value: addonDetails.BuilderTotalContactLimit,
      sourceLabel: "Builder Project",
    };
  }

  if (Number(addonDetails.RequirementTotalContactLimit) > 0) {
    return {
      value: addonDetails.RequirementTotalContactLimit,
      sourceLabel: "Requirement Property",
    };
  }

  return { value: item?.Value || "0", sourceLabel: "" };
};

const getAddonDisplayDates = (item) => {
  const addonCode = item?.AddonAcces;
  const featureSession = item?.FeaturesExpiredsessions;
  const usesFeatureSessionDates = [
    ADDONS.OWNER_PROPERTY,
    ADDONS.BROKER_PROPERTY,
    ADDONS.BUILDER_PROPERTY,
  ].includes(addonCode);

  if (usesFeatureSessionDates && featureSession) {
    return {
      startDate: featureSession?.startDate || "",
      endDate: featureSession?.expiredDate || "",
    };
  }

  return {
    startDate: item?.StartDate || "",
    endDate: item?.EndDate || "",
  };
};

const formatInvoiceDate = (value) => {
  if (!value) return "";
  const dateValue = new Date(value);
  return Number.isNaN(dateValue.getTime()) ? String(value) : dateValue.toLocaleDateString("en-GB");
};

const formatDurationRange = (startDate, endDate) => {
  const start = formatInvoiceDate(startDate);
  const end = formatInvoiceDate(endDate);

  if (start && end) return `${start} to ${end}`;
  if (start) return start;
  if (end) return end;
  return "-";
};

const formatDisplayName = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const formatDateLabel = (value) => {
  if (!value) return "N/A";
  const dateValue = new Date(value);
  return Number.isNaN(dateValue.getTime()) ? "N/A" : dateValue.toLocaleDateString("en-GB");
};

const isDurationAddonAccess = (addonCode) => {
  const code = normalize(addonCode);
  return [
    normalize(ADDONS.OWNER_PROPERTY),
    normalize(ADDONS.BROKER_PROPERTY),
    normalize(ADDONS.BUILDER_PROPERTY),
  ].includes(code);
};

const isCreditAddonAccess = (addonCode) => normalize(addonCode) === normalize(ADDONS.CONATCT_CREDIT);

const isSubUserAddonAccess = (addonCode) => normalize(addonCode) === normalize(ADDONS.SUBUSER_LIMIT);

const getAddonContactLimitFromArray = (item) => {
  const addonLimit = Array.isArray(item?.AddonsArray) ? item.AddonsArray[0] : null;
  if (!addonLimit) return null;

  const limitValue =
    Number(addonLimit?.OwnerTotalContactLimit || 0) ||
    Number(addonLimit?.BrokerTotalContactLimit || 0) ||
    Number(addonLimit?.BuilderTotalContactLimit || 0) ||
    Number(addonLimit?.RequirementTotalContactLimit || 0) ||
    null;

  return limitValue;
};

const getSubscriptionFeatureSessions = (item, featuresAccessData = []) => {
  const featureIds = new Set(
    (Array.isArray(item?.FeaturesAccess) ? item.FeaturesAccess : [])
      .map((entry) => extractId(entry))
      .filter(Boolean)
  );

  const matchedSessions = featuresAccessData.filter((entry) => featureIds.has(extractId(entry)));
  if (matchedSessions.length > 0) {
    return matchedSessions;
  }

  return (Array.isArray(item?.features) ? item.features : []).map((featureName, index) => ({
    _id: `${item?._id || item?.orderId || "feature"}-${index}`,
    FeaturesName: featureName,
    durationInMonth: item?.durationInMonth || item?.DurationMonths || "",
    status: item?.isPending ? "Pending" : "Active",
    expiredDate: item?.expiredDate || "",
  }));
};

const getPlanPresentation = (item, featuresAccessData = []) => {
  if (item?.isPending === true) {
    return {
      tone: "pending",
      tagLabel: "Pending Plan",
      cardTitle: "Future Plan",
    };
  }

  // Check if all matched features are expired
  const featureIds = new Set(
    (Array.isArray(item?.FeaturesAccess) ? item.FeaturesAccess : [])
      .map((entry) => extractId(entry))
      .filter(Boolean)
  );

  if (featureIds.size > 0) {
    const matchedFeatures = featuresAccessData.filter((entry) => featureIds.has(extractId(entry)));
    
    if (matchedFeatures.length > 0) {
      const allExpired = matchedFeatures.every((feature) => {
        const status = String(feature?.status || "").toLowerCase().trim();
        return status === "expried" || status === "expired" || status === "inactive";
      });

      if (allExpired) {
        return {
          tone: "expired",
          tagLabel: "Expired",
          cardTitle: "Expired Plan",
        };
      }

      const anyActive = matchedFeatures.some((feature) => {
        const status = String(feature?.status || "").toLowerCase().trim();
        return status === "active";
      });

      if (anyActive) {
        return {
          tone: "active",
          tagLabel: "Active",
          cardTitle: "Current Plan",
        };
      }
    }
  }

  return {
    tone: "active",
    tagLabel: "Active",
    cardTitle: "Current Plan",
  };
};

const getAddonPresentation = (item) => {
  const status = String(item?.AddonStatus || item?.status || "").toLowerCase().trim();

  if (status === "pending") {
    return {
      tone: "pending",
      tagLabel: "Future Add-ons",
    };
  }

  if (status === "active") {
    return {
      tone: "active",
      tagLabel: "Active",
    };
  }

  return {
    tone: "neutral",
    tagLabel: "",
  };
};

const resolveInvoiceNumber = (item = {}) => (
  item?.InvoiceNo ||
  item?.invoiceNo ||
  item?.invoiceNumber ||
  item?.InvoiceNumber ||
  item?.orderId ||
  item?.razorpayOrderId ||
  item?.razorpayPaymentId ||
  item?._id ||
  "N/A"
);

const getInvoiceCardKey = (item, index, mode) => (
  item?._id || (resolveInvoiceNumber(item) !== "N/A" ? resolveInvoiceNumber(item) : `${mode}-${index}`)
);

const resolveName = (entry) => (
  entry?.FeaturesName || entry?.AddonName || entry?.name || entry?.title || entry?.planName || ""
);

const toCatalogMap = (items = []) => {
  const map = new Map();

  items.forEach((entry) => {
    const id = String(entry?._id || entry?.id || "").trim();
    if (!id) return;
    map.set(id, entry);
  });

  return map;
};

const extractId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    return String(value?._id || value?.id || value?.AddonId || value?.addonId || "").trim();
  }
  return "";
};

const resolveAccessLines = ({ item, paidAddonsData = [] }) => {
  const addonMap = toCatalogMap(paidAddonsData);

  const addonIds = [
    ...(Array.isArray(item?.AddonsId) ? item.AddonsId : []),
    ...(Array.isArray(item?.addonsId) ? item.addonsId : []),
    ...(Array.isArray(item?.addons) ? item.addons : []),
  ]
    .map(extractId)
    .filter(Boolean);

  const seen = new Set();
  const lines = [];

  addonIds.forEach((rawId) => {
    const id = String(rawId || "").trim();
    if (!id || seen.has(`a-${id}`)) return;

    const addon = addonMap.get(id);
    if (!addon) return;

    const name = resolveName(addon);
    if (!name) return;

    seen.add(`a-${id}`);
    const addonDuration = formatDurationRange(
      addon?.FeaturesExpiredsessions?.startDate || addon?.StartDate,
      addon?.FeaturesExpiredsessions?.expiredDate || addon?.EndDate
    );
    const addonAmount = Number(addon?.Amount || addon?.amount || 0);
    lines.push({
      description: `Add-on: ${name}`,
      duration: addonDuration,
      amount: addonAmount,
      includeInTotal: true,
    });
  });

  // Fallback 1: add-on names already present on payment object.
  const addonNames = (Array.isArray(item?.addons) ? item.addons : [])
    .map((entry) => {
      if (typeof entry === "string") return entry;
      return entry?.name || entry?.AddonName || "";
    })
    .map((name) => String(name || "").trim())
    .filter(Boolean);

  addonNames.forEach((name) => {
    if (seen.has(`aname-${name}`)) return;
    seen.add(`aname-${name}`);
    lines.push({ description: `Add-on: ${name}`, duration: "-", amount: 0, includeInTotal: false });
  });

  // Fallback 2: for addon history rows, expose feature session info as add-on detail.
  const featureSessionName = item?.AddonName || item?.FeaturesExpiredsessions?.FeaturesName;
  if (featureSessionName && !seen.has(`sess-${featureSessionName}`)) {
    const sessionDuration = formatDurationRange(
      item?.FeaturesExpiredsessions?.startDate || item?.StartDate,
      item?.FeaturesExpiredsessions?.expiredDate || item?.EndDate
    );
    const sessionAmount = Number(item?.Amount || item?.amount || 0);
    seen.add(`sess-${featureSessionName}`);
    lines.push({
      description: `Add-on: ${featureSessionName}`,
      duration: String(sessionDuration || "-"),
      amount: sessionAmount,
      includeInTotal: true,
    });
  }

  return lines;
};

const resolveSubscriptionAccessItems = (item, featuresAccessData = []) => {
  const sessions = getSubscriptionFeatureSessions(item, featuresAccessData);
  const seen = new Set();

  return sessions
    .map((session) => {
      const name = resolveName(session);
      if (!name) return null;

      const sessionStatus = String(session?.status || "").toLowerCase().trim();
      if (sessionStatus === "pending" && session?.AddonId) return null;

      const duration = session?.durationInMonth || item?.durationInMonth || item?.DurationMonths || "-";
      const expiry = formatDateLabel(session?.expiredDate || item?.expiredDate);
      const value = session?.value || item?.Value || session?.Value || null;
      const key = `${name}-${duration}-${expiry}`;

      if (seen.has(key)) return null;
      seen.add(key);

      return {
        title: formatDisplayName(name),
        value: value,
        meta: value ? value.toString() : null,
        durationInMonth: duration !== "-" ? duration : null,
        expiredDate: expiry !== "N/A" ? expiry : null,
      };
    })
    .filter(Boolean);
};

// Seller (BROX) is registered in Gujarat: intra-state → CGST + SGST, inter-state → IGST.
const SELLER_STATE = "gujarat";
const SELLER_GST_STATE_CODE = "24";

const resolveCustomerState = (item = {}, user = {}) => {
  const raw =
    item?.BillingState ||
    item?.State ||
    item?.state ||
    user?.state ||
    user?.State ||
    user?.userState ||
    "";
  return String(raw?.name || raw || "").trim();
};

// Billing State comes from a dropdown but older records may hold free text, so
// tolerate case, extra spaces, "Gujarat State" and the "GJ" short code.
const isSellerState = (state) => {
  const normalized = String(state || "").trim().toLowerCase().replace(/\s+/g, " ");
  return normalized === "gj" || normalized.split(" ").includes(SELLER_STATE);
};

const isIntraStateSupply = (customerState, customerGst) => {
  if (customerState) {
    return isSellerState(customerState);
  }
  // No state on record: fall back to the GSTIN state code (first two digits).
  const gst = String(customerGst || "").trim();
  if (/^\d{2}/.test(gst)) {
    return gst.slice(0, 2) === SELLER_GST_STATE_CODE;
  }
  // Unknown location: keep the previous CGST + SGST behaviour.
  return true;
};

const buildInvoiceData = (item, mode, user, paidAddonsData = [], featuresAccessData = []) => {
  const isAddon = mode === "addons";
  const customerGst = item?.GSTNumber || user?.UserGSTNO || "N/A";
  const customerState = resolveCustomerState(item, user);
  const isIntraState = isIntraStateSupply(customerState, customerGst);
  const invoiceNumber = resolveInvoiceNumber(item);
  const sourceDate = item?.createdOn || item?.createdAt || item?.StartDate || item?.startDate || new Date().toISOString();
  const addonCode = item?.AddonAcces || item?.AddonAccess || item?.AddonAccesCode || "";
  const addonDurationMonths = Number(
    item?.FeaturesExpiredsessions?.durationInMonth ?? item?.durationInMonth ?? item?.DurationMonths ?? item?.Duration ?? 0
  );
  const addonValue = item?.Value ?? item?.value ?? item?.AddContactCredits ?? item?.AddContactCredit ?? "";
  const totalAmount = Number(item?.amount ?? item?.Amount ?? 0);
  const originalPrice = Number(item?.originalPrice ?? 0);
  const discountAmount = Number(item?.discountAmount ?? 0);
  
  const resolvedAccessLines = isAddon
    ? []
    : resolveSubscriptionAccessItems(item, featuresAccessData);
  
  // Build accessItems from AddonsItems - only show Sub User Limit
  const addonAccessItems = Array.isArray(item?.AddonsItems) 
    ? item.AddonsItems.filter((addonItem) => addonItem && (addonItem.name === "Sub User Limit" || addonItem.AddonName === "Sub User Limit")).map((addonItem) => {
        if (!addonItem) return null;
        return {
          title: String(addonItem?.name || addonItem?.AddonName || "Add-on").trim(),
          meta: addonItem?.value !== undefined && addonItem?.value !== null 
            ? `Value: ${addonItem.value}` 
            : null,
          durationInMonth: null,  // AddonsItems don't have duration info
          expiredDate: null,      // AddonsItems don't have expiry info
        };
      }).filter(Boolean)
    : [];
  
  const mainDescription = isAddon ? (item?.AddonName || "Add-on") : (item?.PlanName || item?.planId?.name || "Premium Plan");
  const startDate = item?.StartDate || item?.FeaturesExpiredsessions?.startDate || "";
  const endDate = item?.EndDate || item?.FeaturesExpiredsessions?.expiredDate || "";
  const showValidity = isAddon && isDurationAddonAccess(addonCode);
  const contactCreditsValue = getAddonContactLimitFromArray(item);
  const addonDisplayLabel = !isAddon
    ? "Duration"
    : isCreditAddonAccess(addonCode)
      ? "Credits"
      : isSubUserAddonAccess(addonCode)
        ? "Sub Users"
        : "Duration";
  const addonDisplayValue = !isAddon
    ? String(item?.durationInMonth || item?.DurationMonths || "1")
    : isDurationAddonAccess(addonCode)
      ? String(addonDurationMonths || 1)
      : isCreditAddonAccess(addonCode)
        ? String(
            contactCreditsValue ??
              addonValue ??
              item?.AddonsArray?.[0]?.OwnerTotalContactLimit ??
              item?.AddonsArray?.[0]?.AddonsArray?.[0]?.BrokerTotalContactLimit ??
              item?.AddonsArray?.[0]?.BuilderTotalContactLimit ??
              item?.AddonsArray?.[0]?.RequirementTotalContactLimit ??
              "N/A"
          )
        : isSubUserAddonAccess(addonCode)
          ? String(addonValue || item?.subUserLimit || "N/A")
          : String(addonDurationMonths || 1);

  // durationInMonth already includes any offer bonus; split it back out so the
  // invoice reads "6 Month(s) + 1 Month(s) FREE" (same wording as the server's).
  const appliedOffer = isAddon ? null : getAppliedOffer(item);
  const bonusMonths = Number(appliedOffer?.bonusMonths || 0);

  // Build the items array with the main item and any AddonsItems
  const mainItem = {
    description: mainDescription,
    durationLabel: addonDisplayLabel,
    duration: appliedOffer
      ? `${appliedOffer.paidMonths} Month(s) + ${bonusMonths} Month(s) FREE`
      : addonDisplayValue,
    amount: totalAmount,
    includeInTotal: true,
    type: "Plan",
  };

  // A ₹0 line so the free months show on the bill; kept out of the totals.
  const offerItem = appliedOffer
    ? {
      description: `${appliedOffer.name || "Special Offer"}: +${bonusMonths} Month(s) FREE`,
      durationLabel: addonDisplayLabel,
      duration: "",
      amount: 0,
      includeInTotal: false,
      type: "Offer",
    }
    : null;

  const addonItems = (item?.AddonsItems || []).map((addonItem) => ({
    description: addonItem.name,
    durationLabel: addonDisplayLabel,
    duration: addonDisplayValue,
    amount: Number(addonItem.amount || 0),
    includeInTotal: false, // Don't include addons in total calculation
    type: addonItem.type || "Addon",
  }));

  return {
    invoiceNumber: String(invoiceNumber),
    date: formatInvoiceDate(sourceDate),
    customerName: item?.BillingName || user?.name || item?.name || "N/A",
    customerGst,
    customerState: customerState || "N/A",
    customerAddress: item?.BillingAddress || user?.address || user?.location || item?.customerAddress || "N/A",
    customerEmail: item?.BillingEmail || user?.email || "N/A",
    paymentMode: item?.paymentMode || (isAddon ? "Razorpay" : "UPI"),
    agentName: item?.agentName || user?.agentName || "System",
    transactionId: item?.razorpayPaymentId || item?.razorpayOrderId || item?.orderId || item?.transactionId || "N/A",
    showSummaryBreakdown: true,
    showAccessSection: true,
    showAddonValidity: showValidity,
    showThankYou: true,
    startDate,
    endDate,
    items: offerItem ? [mainItem, offerItem, ...addonItems] : [mainItem, ...addonItems],
    accessItems: [...resolvedAccessLines, ...addonAccessItems],
    itemColumnLabel: addonDisplayLabel,
    discount: discountAmount,
    originalPrice: originalPrice,
    isAddon: isAddon,
    isIntraState,
    cgstRate: isIntraState ? 9 : 0,
    sgstRate: isIntraState ? 9 : 0,
    igstRate: isIntraState ? 0 : 18,
    taxAmount: Number(item.taxAmount || 0),
    amount: Number(item.amount || 0),
  };
};

const MyPlans = ({
  history = [],
  mode = "subscriptions",
  loading = false,
  user = {},
  paidAddonsData = [],
  featuresAccessData = [],
}) => {
  const invoiceRef = useRef(null);
  const [invoiceRequest, setInvoiceRequest] = useState(null);
  const [invoiceBusyKey, setInvoiceBusyKey] = useState(null);

  useEffect(() => {
    if (!invoiceRequest || !invoiceRef.current) return;

    let cancelled = false;

    const run = async () => {
      try {
        // Wait for all images (e.g. signature) inside the invoice to fully load
        // before snapshotting. On slow CDNs (AWS Amplify) the image may not be
        // ready within a fixed delay, leaving it blank in the generated PDF.
        const images = Array.from(invoiceRef.current.querySelectorAll("img"));
        await Promise.all(
          images.map((img) =>
            img.complete && img.naturalHeight !== 0
              ? Promise.resolve()
              : new Promise((resolve) => {
                  img.addEventListener("load", resolve, { once: true });
                  img.addEventListener("error", resolve, { once: true });
                })
          )
        );

        if (cancelled || !invoiceRef.current) return;

        await html2pdf()
          .set({
            margin: 0,
            filename: invoiceRequest.filename,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 1.35, useCORS: true, backgroundColor: "#ffffff" },
            jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
          })
          .from(invoiceRef.current)
          .save();
      } catch (error) {
        console.error("Invoice PDF generation failed:", error);
      } finally {
        if (!cancelled) {
          setInvoiceRequest(null);
          setInvoiceBusyKey(null);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [invoiceRequest]);

  const handleDownloadInvoice = (item, index) => {
    const cardKey = getInvoiceCardKey(item, index, mode);
    if (invoiceRequest || invoiceBusyKey === cardKey) return;

    const invoiceData = buildInvoiceData(item, mode, user, paidAddonsData, featuresAccessData);
    const filenameNumber = invoiceData.invoiceNumber !== "N/A" ? invoiceData.invoiceNumber : cardKey;
    const filename = `${mode === "addons" ? "addon" : "subscription"}-invoice-${filenameNumber}.pdf`;

    setInvoiceBusyKey(cardKey);
    setInvoiceRequest({ data: invoiceData, filename });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-10 text-center border-2 border-dashed border-gray-100 text-gray-500 font-semibold">
        Loading data...
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="bg-white rounded-3xl p-12 text-center border-2 border-dashed border-gray-100">
        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 text-gray-300">
          <Receipt size={40} />
        </div>
        <h3 className="text-xl font-black text-gray-900">No payment history found</h3>
        <p className="text-gray-500 mt-2 font-medium">Your subscription records will appear here.</p>
      </div>
    );
  }

  const sortedHistory = [...history].sort((a, b) => {
    if (mode === "addons") {
      const addonStatusOrder = { active: 0, pending: 1, "": 2 };
      const aStatus = String(a?.AddonStatus || a?.status || "").toLowerCase().trim();
      const bStatus = String(b?.AddonStatus || b?.status || "").toLowerCase().trim();
      const aOrder = addonStatusOrder[aStatus] ?? addonStatusOrder[""];
      const bOrder = addonStatusOrder[bStatus] ?? addonStatusOrder[""];

      if (aOrder !== bOrder) return aOrder - bOrder;

      return new Date(b.createdOn || b.createdAt || b.StartDate || 0) - new Date(a.createdOn || a.createdAt || a.StartDate || 0);
    }

    const aPending = a?.isPending === true;
    const bPending = b?.isPending === true;
    if (aPending !== bPending) return aPending ? 1 : -1;
    return new Date(b.createdOn || b.createdAt || b.startDate || 0) - new Date(a.createdOn || a.createdAt || a.startDate || 0);
  });

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-10">
      {sortedHistory.map((item, index) => {
        const cardKey = getInvoiceCardKey(item, index, mode);
        if (mode === "addons") {
          const addonDates = getAddonDisplayDates(item);
          const startDate = addonDates.startDate ? new Date(addonDates.startDate).toLocaleDateString("en-GB") : "N/A";
          const endDate = addonDates.endDate ? new Date(addonDates.endDate).toLocaleDateString("en-GB") : "N/A";
          const addonPresentation = getAddonPresentation(item);
          const durationInMonth = item?.FeaturesExpiredsessions?.durationInMonth || item?.durationInMonth;
          const isMappedContactAddon = [
            ADDONS.CONATCT_CREDIT,
            ADDONS.OWNER_PROPERTY,
            ADDONS.BROKER_PROPERTY,
            ADDONS.BUILDER_PROPERTY,
          ].includes(item?.AddonAcces);
          const isSubUserAddon = item?.AddonAcces === ADDONS.SUBUSER_LIMIT;
          const contactInfo = getAddonContactInfo(item);
          const displayValue = isMappedContactAddon ? contactInfo.value : (item.Value || "0");
          const valueLabel = isMappedContactAddon ? "Contact Credits" : "Value";
          const sourceLabel = item?.AddonAcces === ADDONS.CONATCT_CREDIT ? contactInfo.sourceLabel : "";
          const showValidityDates = [
            ADDONS.OWNER_PROPERTY,
            ADDONS.BROKER_PROPERTY,
            ADDONS.BUILDER_PROPERTY,
          ].includes(item?.AddonAcces);

          return (
            <div
              key={item._id || index}
              className="bg-white rounded-3xl p-6 border-2 border-violet-200 shadow-xl shadow-violet-50 relative overflow-hidden"
            >
              <div className="absolute right-6 top-6">
                {addonPresentation.tone === "active" ? (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-wider rounded-full border border-emerald-100">
                    <Zap size={12} fill="currentColor" />
                    {addonPresentation.tagLabel}
                  </span>
                ) : addonPresentation.tone === "pending" ? (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-600 text-[10px] font-black uppercase tracking-wider rounded-full border border-amber-200">
                    <Clock size={12} />
                    {addonPresentation.tagLabel}
                  </span>
                ) : null}
              </div>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-[#503691] text-white">
                  <BadgeInfo size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-black text-gray-900 leading-tight">
                    {item.AddonName || "Add-on"}
                  </h4>
                  {sourceLabel && (
                    <p className="text-xs font-bold text-[#503691] mt-1 uppercase tracking-wide">
                      {sourceLabel}
                    </p>
                  )}
                  {durationInMonth ? (
                    <p className="text-xs font-semibold text-gray-500 mt-1">
                      Duration: {durationInMonth} Month{Number(durationInMonth) > 1 ? "s" : ""}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50/50 rounded-2xl p-3 border border-gray-50">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Amount Paid</p>
                  <p className="text-sm font-bold text-gray-700">{formatCurrency(item.Amount)}</p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-3 border border-gray-50">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{valueLabel}</p>
                  <p className="text-sm font-bold text-gray-700">{displayValue}</p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-3 border border-gray-50">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Original Price</p>
                  <p className="text-sm font-bold text-gray-700">{formatCurrency(item.originalPrice)}</p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-3 border border-gray-50">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Discount</p>
                  <p className="text-sm font-bold text-gray-700">{formatCurrency(item.discountAmount)}</p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-3 border border-gray-50">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Tax</p>
                  <p className="text-sm font-bold text-gray-700">{formatCurrency(item.taxAmount)}</p>
                </div>
              </div>

              {showValidityDates && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className=" rounded-2xl p-3 border border-[#503691]/20">
                    <p className="text-[10px] font-bold text-[#503691] uppercase tracking-wider mb-1">Start Date</p>
                    <p className="text-sm font-bold text-[#503691]">{startDate}</p>
                  </div>
                  <div className=" rounded-2xl p-3 border border-[#503691]/20">
                    <p className="text-[10px] font-bold text-[#503691] uppercase tracking-wider mb-1">End Date</p>
                    <p className="text-sm font-bold text-[#503691]">{endDate}</p>
                  </div>
                </div>
              )}

              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => handleDownloadInvoice(item, index)}
                  disabled={invoiceBusyKey === cardKey}
                  className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-white px-4 py-2 text-sm font-bold text-[#503691] transition-colors hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Download size={16} />
                  {invoiceBusyKey === cardKey ? "Preparing Invoice..." : "Download Invoice"}
                </button>
              </div>

             
            </div>
          );
        }

        const featureSessions = getSubscriptionFeatureSessions(item, featuresAccessData);
        const planPresentation = getPlanPresentation(item, featuresAccessData);
        const isPending = planPresentation.tone === "pending";
        const isActive = planPresentation.tone === "active";
        const isExpired = planPresentation.tone === "expired";
        const planDisplayName = item.PlanName || item.planId?.name || item.planId?.planName || "Premium Plan";
        const primaryFeatureSession = featureSessions.find(
          (feature) => String(feature?.status || "").toLowerCase() !== "pending"
        ) || featureSessions[0];
        const planDuration = primaryFeatureSession?.durationInMonth || item?.durationInMonth || item?.DurationMonths || "-";
        const addons = Array.isArray(item.addons) ? item.addons : [];
        const appliedOffer = getAppliedOffer(item);

        return (
          <div 
            key={item._id || index}
            className={`bg-white rounded-3xl p-6 border-2 transition-all duration-300 relative overflow-hidden ${
              isActive 
                ? "border-[#503691] shadow-xl shadow-[#503691]/20" 
                : isPending
                  ? "border-amber-300 shadow-lg shadow-amber-50"
                  : isExpired
                    ? "border-red-200 shadow-lg shadow-red-50 opacity-75"
                    : "border-gray-100 opacity-80 grayscale-[0.2]"
            }`}
          >
            {/* Status Tag */}
            <div className="absolute top-6 right-6">
              {isActive ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-wider rounded-full border border-emerald-100">
                  <Zap size={12} fill="currentColor" />
                  {planPresentation.tagLabel}
                </span>
              ) : isPending ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-600 text-[10px] font-black uppercase tracking-wider rounded-full border border-amber-200">
                  <Clock size={12} />
                  {planPresentation.tagLabel}
                </span>
              ) : isExpired ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-wider rounded-full border border-red-200">
                  <Clock size={12} />
                  {planPresentation.tagLabel}
                </span>
              ) : (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-400 text-[10px] font-black uppercase tracking-wider rounded-full border border-gray-100">
                  <Clock size={12} />
                  {item.status || "Expired"}
                </span>
              )}
            </div>

            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                isActive ? "bg-[#503691] text-white" : isPending ? "bg-amber-100 text-amber-600" : isExpired ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-400"
              }`}>
                <CreditCard size={24} />
              </div>
              <div>
                <h4 className="text-lg font-black text-gray-900 leading-tight">
                  {planDisplayName}
                </h4>
                <p className={`mt-1 text-xs font-bold uppercase tracking-wide ${
                  isActive ? "text-emerald-600" : isPending ? "text-amber-600" : isExpired ? "text-red-600" : "text-gray-500"
                }`}>
                  {planPresentation.cardTitle}
                </p>
                {appliedOffer && (
                  <span className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                    <span aria-hidden="true">🎁</span>
                    Includes {formatBonusMonths(appliedOffer.bonusMonths)}
                    {appliedOffer.name ? ` (${appliedOffer.name})` : ""}
                  </span>
                )}
              </div>
            </div>

            {/* Details Grid */}
            <div className={`grid gap-4 mb-6 ${isPending ? "grid-cols-2" : "grid-cols-2"}`}>
              <div className="bg-gray-50/50 rounded-2xl p-3 border border-gray-50">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">Duration</p>
                <p className="text-sm font-bold text-gray-700">
                  {planDuration} {Number(planDuration) ? `Month${Number(planDuration) > 1 ? "s" : ""}` : ""}
                </p>
              </div>
              <div className="bg-gray-50/50 rounded-2xl p-3 border border-gray-50">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">Amount Paid</p>
                <p className="text-sm font-bold text-gray-700">{formatCurrency(item.amount)}</p>
              </div>
              {!isPending ? (
                <>
                  <div className="bg-gray-50/50 rounded-2xl p-3 border border-gray-50">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">Started On</p>
                    <p className="text-sm font-bold text-gray-700">
                      {formatDateLabel(primaryFeatureSession?.startDate || item?.startDate)}
                    </p>
                  </div>
                  <div className="bg-gray-50/50 rounded-2xl p-3 border border-gray-50">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">Expires On</p>
                    <p className="text-sm font-bold text-gray-700">
                      {formatDateLabel(primaryFeatureSession?.expiredDate || item?.expiredDate)}
                    </p>
                  </div>
                </>
              ) : null}
            </div>

            {/* Price Breakdown */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-violet-50/60 rounded-xl p-3 border border-violet-100">
                <p className="text-[10px] font-black text-[#503691] uppercase tracking-wider mb-1">Original</p>
                <p className="text-sm font-bold text-[#503691]">{formatCurrency(item.originalPrice)}</p>
              </div>
              <div className="bg-emerald-50/60 rounded-xl p-3 border border-emerald-100">
                <p className="text-[10px] font-black text-emerald-500 uppercase tracking-wider mb-1">Discount</p>
                <p className="text-sm font-bold text-emerald-700">{formatCurrency(item.discountAmount)}</p>
              </div>
              <div className="bg-amber-50/60 rounded-xl p-3 border border-amber-100">
                <p className="text-[10px] font-black text-amber-500 uppercase tracking-wider mb-1">Tax</p>
                <p className="text-sm font-bold text-amber-700">{formatCurrency(item.taxAmount)}</p>
              </div>
            </div>

            {/* Features */}
            {featureSessions.length > 0 && (
              <div className="border-t border-dashed border-gray-100 pt-5 mb-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-3">Features</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {featureSessions.map((feature, idx) => {
                    const featureStatus = String(feature?.status || "").toLowerCase().trim();
                    const isFeaturePending = featureStatus === "pending";
                    const isFeatureExpired = featureStatus === "expried" || featureStatus === "expired" || featureStatus === "inactive";
                    return (
                      <div
                        key={feature?._id || `${feature?.FeaturesName}-${idx}`}
                        className={`rounded-2xl border p-3 ${
                          isFeaturePending
                            ? "border-amber-200 bg-amber-50/50"
                            : isFeatureExpired
                              ? "border-red-200 bg-red-50/50"
                              : "border-sky-100 bg-sky-50/60"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-black text-gray-900">
                              {formatDisplayName(feature?.FeaturesName)}
                            </p>
                            <p className="mt-1 text-xs font-semibold text-gray-500">
                              Duration: {feature?.durationInMonth || "-"} Month{Number(feature?.durationInMonth) > 1 ? "s" : ""}
                            </p>
                            <p className="mt-1 text-xs font-semibold text-gray-500">
                              Expires: {formatDateLabel(feature?.expiredDate)}
                            </p>
                          </div>
                          <span
                            className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${
                              isFeaturePending
                                ? "bg-amber-100 text-amber-700"
                                : isFeatureExpired
                                  ? "bg-red-100 text-red-700"
                                  : "bg-emerald-100 text-emerald-700"
                            }`}
                          >
                            {isFeaturePending ? "Future Plan" : isFeatureExpired ? "Expired" : "Active"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add-ons */}
            {addons.length > 0 && (
              <div className="border-t border-dashed border-gray-100 pt-5">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-3">Included Add-ons</p>
                <div className="flex flex-wrap gap-2">
                  {addons.map((addon, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[11px] font-bold ${
                        isActive ? "bg-[#503691] text-[#503691]" : "bg-gray-50 text-gray-500"
                      }`}
                    >
                      <UserPlus size={12} />
                      {addon.name}: {addon.value} ({formatCurrency(addon.price)})
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => handleDownloadInvoice(item, index)}
                disabled={invoiceBusyKey === cardKey}
                className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-white px-4 py-2 text-sm font-bold text-[#503691] transition-colors hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Download size={16} />
                {invoiceBusyKey === cardKey ? "Preparing Invoice..." : "Download Invoice"}
              </button>
            </div>

           
          </div>
        );
      })}
      </div>

      <div className="pointer-events-none fixed left-[-10000px] top-0">
        {invoiceRequest ? <BROXInvoiceGenerator ref={invoiceRef} data={invoiceRequest.data} /> : null}
      </div>
    </>
  );
};

export default MyPlans;
